import { useState } from 'react';
import ConfigHistoryDialog from '../config/ConfigHistoryDialog';
import { useConfigCard } from '../config/useConfigCard';
import { CardStateFrame, ConfigCardFrame, ConfigSwitch, ForbiddenNotice } from './ConfigCardParts';
import ConfirmChangesDialog, { ChangeLine } from './ConfirmChangesDialog';
import { ZOFEUR_DEFAULTS, ZOFEUR_KEYS } from './financeConfigDefaults';

const K = ZOFEUR_KEYS;
const KEYS = Object.values(ZOFEUR_KEYS);

const LABELS: Record<string, string> = {
  [K.optionEnabled]: 'Zofeur option for customers',
  [K.insuranceNoteEnabled]: 'Insurance note',
};

/**
 * #1251 — Finance → Configuration: the customer's Zofeur driver option at checkout (Spec 072 v2) and
 * the insurance note on legs driven by an insured provider (Spec 072), moved here from Runtime Config.
 * Both keys are saved in one atomic request, with the same audit as Runtime Config. Flipping the
 * Zofeur option always asks first, as the Tabby customer switch does; the insurance note saves
 * without a confirm.
 */
export default function ZofeurCard({ token }: { token: string }) {
  const card = useConfigCard({ keys: KEYS, screen: 'finance-config', defaults: ZOFEUR_DEFAULTS, token });
  const { draft, server } = card;
  const [confirming, setConfirming] = useState(false);
  const [history, setHistory] = useState(false);

  if (card.load.kind === 'loading') {
    return (
      <CardStateFrame testId="zofeur-card" title="Zofeur">
        <div className="admin-loading" aria-live="polite"><div className="admin-loading-spinner" /></div>
      </CardStateFrame>
    );
  }
  if (card.load.kind === 'error') {
    return (
      <CardStateFrame testId="zofeur-card" title="Zofeur">
        <div className="admin-info-banner danger" role="alert" data-testid="zofeur-load-error">{card.load.message}</div>
        <button type="button" className="admin-btn admin-btn-sm admin-btn-outline" onClick={() => card.reload()}>Try again</button>
      </CardStateFrame>
    );
  }
  if (card.load.kind === 'missing') {
    return (
      <CardStateFrame testId="zofeur-card" title="Zofeur">
        <div className="admin-info-banner warning" role="status" data-testid="zofeur-unavailable">
          The Zofeur settings are not available to your account on this environment. Reload the page, or ask a super admin.
        </div>
      </CardStateFrame>
    );
  }

  const changes = card.changesFrom(draft);
  const dirty = changes.length > 0;
  const optionOn = draft[K.optionEnabled] === 'true';
  const serverOptionOn = server[K.optionEnabled] === 'true';

  const changeLines = (): ChangeLine[] => changes.map((c) => ({
    label: LABELS[c.key],
    from: server[c.key] === 'true' ? 'On' : 'Off',
    to: c.value === 'true' ? 'On' : 'Off',
  }));

  const submit = () => {
    if (!dirty) return;
    if (optionOn !== serverOptionOn) setConfirming(true);
    else void card.save(changes);
  };

  return (
    <>
      <ConfigCardFrame
        testId="zofeur-card"
        title="Zofeur"
        status={(
          <span className={`admin-badge ${serverOptionOn ? 'approved' : 'neutral'}`} data-testid="zofeur-status">
            {serverOptionOn ? 'On for customers' : 'Off for customers'}
          </span>
        )}
        dirty={dirty}
        sub="The Zofeur driver option at checkout, next to the recovery truck, and its insurance note."
        lastChanged={card.lastChanged}
        onHistory={() => setHistory(true)}
        footNote="Live within a minute. Same audit as Runtime Config."
        footError={card.formError}
        invalidCount={0}
        saving={card.saving}
        canSave={dirty && !card.forbidden}
        onDiscard={card.discard}
        onSubmit={submit}
        toast={card.toast}
      >
        {card.forbidden && <ForbiddenNotice onReload={() => card.reload()} />}

        <ConfigSwitch
          id="zofeur-option-enabled"
          checked={optionOn}
          onChange={(on) => card.setField(K.optionEnabled, on ? 'true' : 'false')}
          label="Zofeur driver option for customers"
          description="Off hides the Zofeur option at checkout and new Zofeur bookings are refused. Bookings already made keep their driver."
        />
        <ConfigSwitch
          id="insurance-note-enabled"
          checked={draft[K.insuranceNoteEnabled] === 'true'}
          onChange={(on) => card.setField(K.insuranceNoteEnabled, on ? 'true' : 'false')}
          label="Insurance note on insured provider legs"
          description="Off hides the insurance note on every order."
        />
      </ConfigCardFrame>

      {confirming && (
        <ConfirmChangesDialog
          title={optionOn ? 'Offer Zofeur at checkout?' : 'Hide Zofeur at checkout?'}
          body={optionOn
            ? 'Customers can pick a Zofeur driver at checkout within a minute.'
            : 'The Zofeur option disappears from checkout within a minute and new Zofeur bookings are refused. Bookings already made keep their driver.'}
          confirmLabel={optionOn ? 'Save and turn on' : 'Save and turn off'}
          changes={changeLines()}
          onCancel={() => setConfirming(false)}
          onConfirm={() => { setConfirming(false); void card.save(changes); }}
        />
      )}
      {history && (
        <ConfigHistoryDialog
          title="Zofeur history"
          keys={KEYS}
          labels={LABELS}
          token={token}
          onClose={() => setHistory(false)}
        />
      )}
    </>
  );
}
